import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Store, MapPin, Clock, Truck, CreditCard, QrCode, Share2, Copy, ExternalLink, Gift, Star, Palette, Check, Upload, Camera, Loader2, Image as ImageIcon, Printer, Search, Navigation, Volume2, VolumeX, Play, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import soundService from '@/services/soundService';

const DAYS = [
  { key: 'monday', label: 'Segunda' },
  { key: 'tuesday', label: 'Terça' },
  { key: 'wednesday', label: 'Quarta' },
  { key: 'thursday', label: 'Quinta' },
  { key: 'friday', label: 'Sexta' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const THEME_PRESETS = [
  { 
    id: 'orange', 
    name: 'Laranja', 
    primary: '#ff6b00', 
    secondary: '#ff8533', 
    background: '#1a1a2e', 
    card: '#13131f',
    text: '#ffffff',
    accent: '#ff6b00'
  },
  { 
    id: 'blue', 
    name: 'Azul', 
    primary: '#3b82f6', 
    secondary: '#60a5fa', 
    background: '#0f172a', 
    card: '#1e293b',
    text: '#ffffff',
    accent: '#3b82f6'
  },
  { 
    id: 'green', 
    name: 'Verde', 
    primary: '#22c55e', 
    secondary: '#4ade80', 
    background: '#0f1f17', 
    card: '#14261c',
    text: '#ffffff',
    accent: '#22c55e'
  },
  { 
    id: 'purple', 
    name: 'Roxo', 
    primary: '#a855f7', 
    secondary: '#c084fc', 
    background: '#1a0f2e', 
    card: '#231740',
    text: '#ffffff',
    accent: '#a855f7'
  },
  { 
    id: 'red', 
    name: 'Vermelho', 
    primary: '#ef4444', 
    secondary: '#f87171', 
    background: '#1f0f0f', 
    card: '#2a1515',
    text: '#ffffff',
    accent: '#ef4444'
  },
  { 
    id: 'pink', 
    name: 'Rosa', 
    primary: '#ec4899', 
    secondary: '#f472b6', 
    background: '#1f0f1a', 
    card: '#2a1525',
    text: '#ffffff',
    accent: '#ec4899'
  },
  { 
    id: 'gold', 
    name: 'Dourado', 
    primary: '#f59e0b', 
    secondary: '#fbbf24', 
    background: '#1a1810', 
    card: '#252015',
    text: '#ffffff',
    accent: '#f59e0b'
  },
  { 
    id: 'teal', 
    name: 'Turquesa', 
    primary: '#14b8a6', 
    secondary: '#2dd4bf', 
    background: '#0f1f1d', 
    card: '#142926',
    text: '#ffffff',
    accent: '#14b8a6'
  },
  { 
    id: 'light-orange', 
    name: 'Claro Laranja', 
    primary: '#ea580c', 
    secondary: '#f97316', 
    background: '#fef7f0', 
    card: '#ffffff',
    text: '#1a1a1a',
    accent: '#ea580c'
  },
  { 
    id: 'light-blue', 
    name: 'Claro Azul', 
    primary: '#2563eb', 
    secondary: '#3b82f6', 
    background: '#f0f7ff', 
    card: '#ffffff',
    text: '#1a1a1a',
    accent: '#2563eb'
  },
  { 
    id: 'light-green', 
    name: 'Claro Verde', 
    primary: '#16a34a', 
    secondary: '#22c55e', 
    background: '#f0fdf4', 
    card: '#ffffff',
    text: '#1a1a1a',
    accent: '#16a34a'
  },
  { 
    id: 'minimal', 
    name: 'Minimalista', 
    primary: '#000000', 
    secondary: '#333333', 
    background: '#ffffff', 
    card: '#f5f5f5',
    text: '#1a1a1a',
    accent: '#000000'
  }
];

const Settings = () => {
  const { user, token } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  useEffect(() => {
    fetchStore();
  }, []);

  const fetchStore = async () => {
    try {
      const res = await axios.get(`${API}/stores/me`);
      setStore(res.data);
    } catch (e) {
      toast.error('Erro ao carregar dados da loja');
    } finally {
      setLoading(false);
    }
  };

  // Upload de logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use fetch instead of axios for FormData to avoid header issues
      const response = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      const logoUrl = data.url;
      
      // Update local state
      setStore(prev => ({ ...prev, logo_url: logoUrl }));
      
      // Save to backend
      await axios.put(`${API}/stores/me`, { logo_url: logoUrl });
      toast.success('Logo atualizada!');
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Erro ao fazer upload da logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Upload de banner
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB');
      return;
    }
    
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use fetch instead of axios for FormData to avoid header issues
      const response = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      const bannerUrl = data.url;
      
      // Update local state
      setStore(prev => ({ ...prev, banner_url: bannerUrl }));
      
      // Save to backend
      await axios.put(`${API}/stores/me`, { banner_url: bannerUrl });
      toast.success('Banner atualizado!');
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Erro ao fazer upload do banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send valid fields to avoid validation errors
      const updateData = {};
      
      if (store.name) updateData.name = store.name;
      if (store.slug) updateData.slug = store.slug;
      if (store.whatsapp) updateData.whatsapp = store.whatsapp;
      // Always preserve logo_url and banner_url even if empty string
      updateData.logo_url = store.logo_url || '';
      updateData.banner_url = store.banner_url || '';
      if (store.address) updateData.address = store.address;
      if (store.schedule) updateData.schedule = store.schedule;
      if (store.delivery_enabled !== undefined) updateData.delivery_enabled = store.delivery_enabled;
      if (store.pickup_enabled !== undefined) updateData.pickup_enabled = store.pickup_enabled;
      if (store.delivery_fee) updateData.delivery_fee = store.delivery_fee;
      if (store.pix_config) updateData.pix_config = store.pix_config;
      if (store.loyalty_config) updateData.loyalty_config = store.loyalty_config;
      if (store.theme_config) updateData.theme_config = store.theme_config;
      if (store.print_config) updateData.print_config = store.print_config;
      if (store.notification_config) updateData.notification_config = store.notification_config;
      
      await axios.put(`${API}/stores/me`, updateData);
      toast.success('Configurações salvas');
    } catch (e) {
      console.error('Error saving:', e.response?.data || e.message);
      toast.error('Erro ao salvar: ' + (e.response?.data?.detail || 'Tente novamente'));
    } finally {
      setSaving(false);
    }
  };

  const updateStore = (field, value) => {
    setStore(prev => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field, value) => {
    setStore(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  // Formatar CEP com máscara (00000-000)
  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  };

  // Buscar endereço por CEP
  const fetchAddressByCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('Digite um CEP válido com 8 dígitos');
      return;
    }
    
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado. Verifique se o CEP está correto.');
        return;
      }
      
      const newAddress = {
        cep: cleanCep,
        street: data.logradouro || store.address?.street || '',
        neighborhood: data.bairro || store.address?.neighborhood || '',
        city: data.localidade || store.address?.city || '',
        state: data.uf || store.address?.state || '',
        number: store.address?.number || '',
        latitude: null,
        longitude: null
      };
      
      // Try to geocode using Google Maps
      if (window.google && window.google.maps) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          const fullAddress = `${newAddress.street}, ${newAddress.neighborhood}, ${newAddress.city}, ${newAddress.state}, Brasil`;
          
          geocoder.geocode({ address: fullAddress }, (results, status) => {
            if (status === 'OK' && results[0]) {
              newAddress.latitude = results[0].geometry.location.lat();
              newAddress.longitude = results[0].geometry.location.lng();
              
              setStore(prev => ({
                ...prev,
                address: { ...prev.address, ...newAddress }
              }));
              toast.success('Endereço e coordenadas encontrados! Complete o número e salve.');
            } else {
              setStore(prev => ({
                ...prev,
                address: { ...prev.address, ...newAddress }
              }));
              toast.success('Endereço encontrado! Use a busca do Google Maps para obter as coordenadas.');
            }
          });
        } catch (geocodeError) {
          setStore(prev => ({
            ...prev,
            address: { ...prev.address, ...newAddress }
          }));
          toast.success('Endereço encontrado! Complete o número e salve.');
        }
      } else {
        setStore(prev => ({
          ...prev,
          address: { ...prev.address, ...newAddress }
        }));
        toast.success('Endereço encontrado! Complete o número e salve.');
      }
    } catch (e) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  // Handle Google Maps address selection
  const handleAddressSelect = (addressData) => {
    setStore(prev => ({
      ...prev,
      address: {
        ...prev.address,
        street: addressData.street || '',
        number: addressData.number || prev.address?.number || '',
        neighborhood: addressData.neighborhood || '',
        city: addressData.city || '',
        state: addressData.state || '',
        cep: addressData.cep || prev.address?.cep || '',
        latitude: addressData.latitude || null,
        longitude: addressData.longitude || null
      }
    }));
    toast.success('Endereço encontrado! Complete o número se necessário e salve.');
  };

  const updateSchedule = (day, field, value) => {
    setStore(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: { ...prev.schedule[day], [field]: value }
      }
    }));
  };

  const updateDeliveryFee = (field, value) => {
    setStore(prev => ({
      ...prev,
      delivery_fee: { ...prev.delivery_fee, [field]: value }
    }));
  };

  const updatePixConfig = (field, value) => {
    setStore(prev => ({
      ...prev,
      pix_config: { ...prev.pix_config, [field]: value }
    }));
  };

  const updateLoyaltyConfig = (field, value) => {
    setStore(prev => ({
      ...prev,
      loyalty_config: { ...(prev.loyalty_config || {}), [field]: value }
    }));
  };

  const updateThemeConfig = (field, value) => {
    setStore(prev => ({
      ...prev,
      theme_config: { ...(prev.theme_config || {}), [field]: value }
    }));
  };

  const applyThemePreset = (preset) => {
    const theme = THEME_PRESETS.find(t => t.id === preset);
    if (theme) {
      setStore(prev => ({
        ...prev,
        theme_config: {
          preset: theme.id,
          primary_color: theme.primary,
          secondary_color: theme.secondary,
          background_color: theme.background,
          card_color: theme.card,
          text_color: theme.text,
          accent_color: theme.accent
        }
      }));
    }
  };

  const copyStoreLink = () => {
    const link = `${window.location.origin}/${store.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/${store.slug}`;
    const text = `Confira nosso cardápio digital: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading || !store) {
    return (
      <DashboardLayout title="Configurações">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Configurações">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Store Link */}
        <Card className="card-adegga bg-[#ff6b00]/10 border-[#ff6b00]/30">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white/60">Link do seu cardápio</p>
                <p className="text-[#ff6b00] font-mono break-all">
                  {window.location.origin}/{store.slug}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={copyStoreLink} className="bg-white/10">
                  <Copy className="w-4 h-4 mr-1" />
                  Copiar
                </Button>
                <Button size="sm" variant="secondary" onClick={shareWhatsApp} className="bg-green-600 hover:bg-green-700">
                  <Share2 className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
                <a href={`/${store.slug}`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="secondary" className="bg-white/10">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="store" className="space-y-6">
          <TabsList className="bg-[#0f0f15] flex-wrap">
            <TabsTrigger value="store" className="data-[state=active]:bg-[#ff6b00]">Loja</TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-[#ff6b00]">Horários</TabsTrigger>
            <TabsTrigger value="delivery" className="data-[state=active]:bg-[#ff6b00]">Entrega</TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-[#ff6b00]">Pagamento</TabsTrigger>
            <TabsTrigger value="loyalty" className="data-[state=active]:bg-[#ff6b00]">Fidelidade</TabsTrigger>
            <TabsTrigger value="theme" className="data-[state=active]:bg-[#ff6b00]">Tema</TabsTrigger>
            <TabsTrigger value="sound" className="data-[state=active]:bg-[#ff6b00]">Som</TabsTrigger>
            <TabsTrigger value="print" className="data-[state=active]:bg-[#ff6b00]">Impressão</TabsTrigger>
          </TabsList>

          {/* Store Info */}
          <TabsContent value="store">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Store className="w-5 h-5 text-[#ff6b00]" />
                  Informações da Loja
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo e Banner Upload */}
                <div className="space-y-4 pb-4 border-b border-white/10">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-[#ff6b00]" />
                    Logo e Banner
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Logo Upload */}
                    <div className="flex flex-col items-center gap-3">
                      <div 
                        className="relative w-24 h-24 rounded-full bg-[#0f0f15] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#ff6b00] transition-colors"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-8 h-8 text-[#ff6b00] animate-spin" />
                        ) : store.logo_url ? (
                          <img 
                            src={store.logo_url.startsWith('/') ? `${API.replace('/api', '')}${store.logo_url}` : store.logo_url} 
                            alt="Logo" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Camera className="w-8 h-8 text-white/40" />
                        )}
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                        />
                      </div>
                      <span className="text-sm text-white/60">Logo (1:1)</span>
                    </div>

                    {/* Banner Upload */}
                    <div className="flex-1 flex flex-col gap-3">
                      <div 
                        className="relative w-full h-32 rounded-lg bg-[#0f0f15] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#ff6b00] transition-colors"
                        onClick={() => bannerInputRef.current?.click()}
                      >
                        {uploadingBanner ? (
                          <Loader2 className="w-8 h-8 text-[#ff6b00] animate-spin" />
                        ) : store.banner_url ? (
                          <img 
                            src={store.banner_url.startsWith('/') ? `${API.replace('/api', '')}${store.banner_url}` : store.banner_url} 
                            alt="Banner" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center">
                            <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                            <span className="text-sm text-white/40">Clique para adicionar banner</span>
                          </div>
                        )}
                        <input
                          ref={bannerInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleBannerUpload}
                        />
                      </div>
                      <span className="text-sm text-white/60">Banner (16:9 recomendado)</span>
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Nome da Loja</label>
                    <Input
                      value={store.name}
                      onChange={(e) => updateStore('name', e.target.value)}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">WhatsApp</label>
                    <Input
                      value={store.whatsapp}
                      onChange={(e) => updateStore('whatsapp', e.target.value)}
                      placeholder="11999999999"
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#ff6b00]" />
                    Endereço da Loja
                  </h4>
                  
                  <p className="text-sm text-white/60">
                    O endereço é essencial para calcular a taxa de entrega por km. Preencha corretamente!
                  </p>
                  
                  {/* Google Maps Autocomplete */}
                  <AddressAutocomplete
                    value={store.address?.street ? `${store.address.street}, ${store.address.number || ''} - ${store.address.neighborhood || ''}, ${store.address.city || ''}` : ''}
                    onChange={handleAddressSelect}
                    placeholder="Pesquisar endereço no Google Maps..."
                  />
                  
                  {/* Ou buscar por CEP */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-[#13131f] px-2 text-white/40">ou digite o CEP</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="CEP (ex: 01310-100)"
                      value={formatCep(store.address?.cep || '')}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, '');
                        updateAddress('cep', rawValue);
                      }}
                      className="flex-1 bg-[#0f0f15] border-white/10 text-white"
                      maxLength={9}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        const cepValue = String(store.address?.cep || '').replace(/\D/g, '');
                        if (cepValue.length === 8) {
                          fetchAddressByCep(cepValue);
                        } else {
                          toast.error('Digite um CEP válido com 8 dígitos');
                        }
                      }}
                      disabled={cepLoading}
                      className="btn-secondary"
                    >
                      {cepLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Search className="w-4 h-4 mr-1" /> Buscar</>
                      )}
                    </Button>
                  </div>
                  
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Input
                      placeholder="Rua"
                      value={store.address?.street || ''}
                      onChange={(e) => updateAddress('street', e.target.value)}
                      className="sm:col-span-2 bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Input
                      placeholder="Número"
                      value={store.address?.number || ''}
                      onChange={(e) => updateAddress('number', e.target.value)}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      placeholder="Bairro"
                      value={store.address?.neighborhood || ''}
                      onChange={(e) => updateAddress('neighborhood', e.target.value)}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Input
                      placeholder="Cidade"
                      value={store.address?.city || ''}
                      onChange={(e) => updateAddress('city', e.target.value)}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                  
                  {/* Coordenadas info */}
                  {store.address?.latitude && store.address?.longitude ? (
                    <div className="flex items-center gap-2 text-sm text-green-400 p-3 bg-green-500/10 rounded-lg">
                      <Check className="w-4 h-4" />
                      <span>Coordenadas configuradas: {store.address.latitude.toFixed(6)}, {store.address.longitude.toFixed(6)}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-400 p-3 bg-yellow-500/10 rounded-lg">
                      ⚠️ Use a busca pelo Google Maps ou CEP para obter as coordenadas automaticamente. As coordenadas são necessárias para o cálculo de entrega por km.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule */}
          <TabsContent value="schedule">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#ff6b00]" />
                  Horários de Funcionamento
                </CardTitle>
                <p className="text-sm text-white/50">Configure os horários de funcionamento da sua loja</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {DAYS.map((day, index) => {
                  const schedule = store.schedule?.[day.key] || { active: true, open: '08:00', close: '22:00' };
                  const is24h = schedule.is_24h || false;
                  const overnight = schedule.overnight || false;
                  
                  return (
                    <div key={day.key} className="p-4 bg-[#0f0f15] rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={schedule.active !== false}
                            onCheckedChange={(v) => updateSchedule(day.key, 'active', v)}
                          />
                          <span className="text-white font-medium w-20">{day.label}</span>
                        </div>
                        
                        {schedule.active !== false && (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={is24h}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setStore(prev => ({
                                      ...prev,
                                      schedule: {
                                        ...prev.schedule,
                                        [day.key]: { 
                                          ...prev.schedule?.[day.key], 
                                          is_24h: true, 
                                          overnight: false,
                                          open: '00:00', 
                                          close: '23:59' 
                                        }
                                      }
                                    }));
                                  } else {
                                    updateSchedule(day.key, 'is_24h', false);
                                  }
                                }}
                                className="w-4 h-4 rounded border-white/20 bg-[#0a0a0f] text-[#ff6b00] focus:ring-[#ff6b00]"
                              />
                              <span className="text-sm text-[#ff6b00]">24h</span>
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {schedule.active !== false && !is24h && (
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-white/40 text-sm">Abre às</span>
                            <Input
                              type="time"
                              value={schedule.open || '08:00'}
                              onChange={(e) => updateSchedule(day.key, 'open', e.target.value)}
                              className="w-28 bg-[#0a0a0f] border-white/10 text-white"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/40 text-sm">Fecha às</span>
                            <Input
                              type="time"
                              value={schedule.close || '22:00'}
                              onChange={(e) => updateSchedule(day.key, 'close', e.target.value)}
                              className="w-28 bg-[#0a0a0f] border-white/10 text-white"
                            />
                            <label className="flex items-center gap-2 cursor-pointer ml-2">
                              <input
                                type="checkbox"
                                checked={overnight}
                                onChange={(e) => updateSchedule(day.key, 'overnight', e.target.checked)}
                                className="w-4 h-4 rounded border-white/20 bg-[#0a0a0f] text-[#ff6b00] focus:ring-[#ff6b00]"
                              />
                              <span className="text-xs text-yellow-400">Madrugada (+1 dia)</span>
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {schedule.active !== false && is24h && (
                        <div className="text-sm text-green-400 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Funcionamento 24 horas
                        </div>
                      )}
                      
                      {schedule.active !== false && !is24h && overnight && (
                        <div className="text-xs text-yellow-400 bg-yellow-500/10 p-2 rounded">
                          Exemplo: {day.label} às {schedule.open || '13:00'} até {schedule.close || '03:00'} de {DAYS[(index + 1) % 7].label}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <div className="p-4 bg-[#ff6b00]/10 border border-[#ff6b00]/20 rounded-lg">
                  <h4 className="text-[#ff6b00] font-medium mb-2">Como configurar:</h4>
                  <ul className="text-sm text-white/60 space-y-1">
                    <li>• <strong>24h:</strong> Marque para ficar aberto o dia inteiro</li>
                    <li>• <strong>Madrugada:</strong> Marque se o horário de fechamento passa da meia-noite (ex: 13h às 3h)</li>
                    <li>• Horários normais (ex: 08:00 às 22:00) não precisam marcar madrugada</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Delivery */}
          <TabsContent value="delivery">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#ff6b00]" />
                  Configurações de Entrega
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <p className="text-white font-medium">Entrega</p>
                    <p className="text-sm text-white/60">Habilitar entrega na loja</p>
                  </div>
                  <Switch
                    checked={store.delivery_enabled}
                    onCheckedChange={(v) => updateStore('delivery_enabled', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <p className="text-white font-medium">Retirada no Local</p>
                    <p className="text-sm text-white/60">Permitir retirada no balcão</p>
                  </div>
                  <Switch
                    checked={store.pickup_enabled}
                    onCheckedChange={(v) => updateStore('pickup_enabled', v)}
                  />
                </div>

                {store.delivery_enabled && (
                  <div className="space-y-4">
                    <h4 className="text-white font-medium">Taxa de Entrega</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Select
                        value={store.delivery_fee?.type || 'fixed'}
                        onValueChange={(v) => updateDeliveryFee('type', v)}
                      >
                        <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f0f15] border-white/10">
                          <SelectItem value="fixed">Taxa Fixa</SelectItem>
                          <SelectItem value="per_km">Por Quilômetro (R$/km)</SelectItem>
                          <SelectItem value="radius">Por Faixas de Distância</SelectItem>
                          <SelectItem value="neighborhood">Por Bairro</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {store.delivery_fee?.type === 'fixed' && (
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Valor da taxa"
                          value={store.delivery_fee?.fixed_value || ''}
                          onChange={(e) => updateDeliveryFee('fixed_value', parseFloat(e.target.value) || 0)}
                          className="bg-[#0f0f15] border-white/10 text-white"
                        />
                      )}
                    </div>
                    
                    {/* Per KM Config */}
                    {store.delivery_fee?.type === 'per_km' && (
                      <div className="space-y-4 p-4 bg-[#0f0f15] rounded-lg">
                        <p className="text-sm text-white/60">A taxa será calculada automaticamente com base na distância entre sua loja e o endereço do cliente.</p>
                        
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm text-white/60 mb-1 block">Valor por km</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">R$</span>
                              <Input
                                type="number"
                                step="0.50"
                                min="0"
                                placeholder="3.00"
                                value={store.delivery_fee?.price_per_km || 3}
                                onChange={(e) => updateDeliveryFee('price_per_km', parseFloat(e.target.value) || 3)}
                                className="pl-10 bg-[#0f0f15] border-white/10 text-white"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-white/60 mb-1 block">Taxa mínima</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">R$</span>
                              <Input
                                type="number"
                                step="0.50"
                                min="0"
                                placeholder="5.00"
                                value={store.delivery_fee?.min_fee || 5}
                                onChange={(e) => updateDeliveryFee('min_fee', parseFloat(e.target.value) || 5)}
                                className="pl-10 bg-[#0f0f15] border-white/10 text-white"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-white/60 mb-1 block">Distância máxima</label>
                            <div className="relative">
                              <Input
                                type="number"
                                step="1"
                                min="1"
                                placeholder="20"
                                value={store.delivery_fee?.max_distance_km || 20}
                                onChange={(e) => updateDeliveryFee('max_distance_km', parseFloat(e.target.value) || 20)}
                                className="pr-10 bg-[#0f0f15] border-white/10 text-white"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">km</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-[#ff6b00]/10 border border-[#ff6b00]/30 rounded-lg p-3">
                          <p className="text-sm text-white/80">
                            <strong>Exemplo:</strong> Cliente a 5km de distância pagará{' '}
                            <span className="text-[#ff6b00] font-bold">
                              R$ {Math.max(store.delivery_fee?.min_fee || 5, 5 * (store.delivery_fee?.price_per_km || 3)).toFixed(2)}
                            </span>
                          </p>
                        </div>
                        
                        <p className="text-xs text-white/40">
                          ⚠️ Certifique-se de que o endereço da sua loja está preenchido corretamente na aba "Loja" para o cálculo funcionar.
                        </p>
                      </div>
                    )}
                    
                    {/* Radius Config */}
                    {store.delivery_fee?.type === 'radius' && (
                      <div className="space-y-3">
                        <p className="text-sm text-white/60">Configure a taxa por faixas de distância:</p>
                        {(store.delivery_fee?.radius_config || []).map((config, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <span className="text-white/60 text-sm">Até</span>
                            <Input
                              type="number"
                              placeholder="km"
                              value={config.km || ''}
                              onChange={(e) => {
                                const newConfig = [...(store.delivery_fee?.radius_config || [])];
                                newConfig[i] = { ...newConfig[i], km: parseFloat(e.target.value) || 0 };
                                updateDeliveryFee('radius_config', newConfig);
                              }}
                              className="w-20 bg-[#0f0f15] border-white/10 text-white"
                            />
                            <span className="text-white/60 text-sm">km = R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Taxa"
                              value={config.fee || ''}
                              onChange={(e) => {
                                const newConfig = [...(store.delivery_fee?.radius_config || [])];
                                newConfig[i] = { ...newConfig[i], fee: parseFloat(e.target.value) || 0 };
                                updateDeliveryFee('radius_config', newConfig);
                              }}
                              className="w-24 bg-[#0f0f15] border-white/10 text-white"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400"
                              onClick={() => {
                                const newConfig = (store.delivery_fee?.radius_config || []).filter((_, j) => j !== i);
                                updateDeliveryFee('radius_config', newConfig);
                              }}
                            >
                              Remover
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/10"
                          onClick={() => {
                            const newConfig = [...(store.delivery_fee?.radius_config || []), { km: 5, fee: 5 }];
                            updateDeliveryFee('radius_config', newConfig);
                          }}
                        >
                          + Adicionar Faixa
                        </Button>
                      </div>
                    )}
                    
                    {/* Neighborhood Config */}
                    {store.delivery_fee?.type === 'neighborhood' && (
                      <div className="space-y-3">
                        <p className="text-sm text-white/60">Configure a taxa por bairro:</p>
                        {(store.delivery_fee?.neighborhoods || []).map((config, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <Input
                              placeholder="Nome do bairro"
                              value={config.name || ''}
                              onChange={(e) => {
                                const newConfig = [...(store.delivery_fee?.neighborhoods || [])];
                                newConfig[i] = { ...newConfig[i], name: e.target.value };
                                updateDeliveryFee('neighborhoods', newConfig);
                              }}
                              className="flex-1 bg-[#0f0f15] border-white/10 text-white"
                            />
                            <span className="text-white/60 text-sm">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Taxa"
                              value={config.fee || ''}
                              onChange={(e) => {
                                const newConfig = [...(store.delivery_fee?.neighborhoods || [])];
                                newConfig[i] = { ...newConfig[i], fee: parseFloat(e.target.value) || 0 };
                                updateDeliveryFee('neighborhoods', newConfig);
                              }}
                              className="w-24 bg-[#0f0f15] border-white/10 text-white"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400"
                              onClick={() => {
                                const newConfig = (store.delivery_fee?.neighborhoods || []).filter((_, j) => j !== i);
                                updateDeliveryFee('neighborhoods', newConfig);
                              }}
                            >
                              Remover
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/10"
                          onClick={() => {
                            const newConfig = [...(store.delivery_fee?.neighborhoods || []), { name: '', fee: 5 }];
                            updateDeliveryFee('neighborhoods', newConfig);
                          }}
                        >
                          + Adicionar Bairro
                        </Button>
                        <div className="mt-4">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Taxa padrão (bairros não listados)"
                            value={store.delivery_fee?.default_fee || ''}
                            onChange={(e) => updateDeliveryFee('default_fee', parseFloat(e.target.value) || null)}
                            className="bg-[#0f0f15] border-white/10 text-white"
                          />
                          <p className="text-xs text-white/40 mt-1">Deixe vazio para não entregar em bairros não listados</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment */}
          <TabsContent value="payment">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-[#ff6b00]" />
                  Configuração do Pix
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Select
                    value={store.pix_config?.key_type || 'cpf'}
                    onValueChange={(v) => updatePixConfig('key_type', v)}
                  >
                    <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                      <SelectValue placeholder="Tipo da chave" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10">
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="random">Chave Aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Chave Pix"
                    value={store.pix_config?.key || ''}
                    onChange={(e) => updatePixConfig('key', e.target.value)}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
                <Input
                  placeholder="Nome do titular"
                  value={store.pix_config?.holder_name || ''}
                  onChange={(e) => updatePixConfig('holder_name', e.target.value)}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
                <p className="text-sm text-white/40">
                  A chave Pix será exibida para o cliente no momento do checkout.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Loyalty Program */}
          <TabsContent value="loyalty">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#ff6b00]" />
                  Programa de Fidelidade
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <p className="text-white font-medium">Ativar Programa de Fidelidade</p>
                    <p className="text-sm text-white/60">Clientes acumulam pontos a cada pedido concluído</p>
                  </div>
                  <Switch
                    checked={store.loyalty_config?.enabled || false}
                    onCheckedChange={(v) => updateLoyaltyConfig('enabled', v)}
                  />
                </div>

                {store.loyalty_config?.enabled && (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-white/60 mb-1 block">Pontos por R$ gasto</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="1"
                          value={store.loyalty_config?.points_per_real || 1}
                          onChange={(e) => updateLoyaltyConfig('points_per_real', parseFloat(e.target.value) || 1)}
                          className="bg-[#0f0f15] border-white/10 text-white"
                        />
                        <p className="text-xs text-white/40 mt-1">Ex: 1 = 1 ponto para cada R$1 gasto</p>
                      </div>
                      <div>
                        <label className="text-sm text-white/60 mb-1 block">Valor de cada ponto (R$)</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.01"
                          value={store.loyalty_config?.redemption_value || 0.01}
                          onChange={(e) => updateLoyaltyConfig('redemption_value', parseFloat(e.target.value) || 0.01)}
                          className="bg-[#0f0f15] border-white/10 text-white"
                        />
                        <p className="text-xs text-white/40 mt-1">Ex: 0.01 = cada ponto vale R$0,01</p>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-white/60 mb-1 block">Mínimo de pontos para resgate</label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="100"
                          value={store.loyalty_config?.min_points_redemption || 100}
                          onChange={(e) => updateLoyaltyConfig('min_points_redemption', parseInt(e.target.value) || 100)}
                          className="bg-[#0f0f15] border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white/60 mb-1 block">Bônus de boas-vindas (pontos)</label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={store.loyalty_config?.welcome_bonus || 0}
                          onChange={(e) => updateLoyaltyConfig('welcome_bonus', parseInt(e.target.value) || 0)}
                          className="bg-[#0f0f15] border-white/10 text-white"
                        />
                        <p className="text-xs text-white/40 mt-1">Pontos extras no primeiro pedido</p>
                      </div>
                    </div>

                    <div className="bg-[#ff6b00]/10 border border-[#ff6b00]/30 rounded-lg p-4">
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <Gift className="w-4 h-4 text-[#ff6b00]" />
                        Como funciona:
                      </h4>
                      <ul className="text-sm text-white/60 space-y-1">
                        <li>• Cliente faz pedido de R$ 50 → ganha {Math.round(50 * (store.loyalty_config?.points_per_real || 1))} pontos</li>
                        <li>• Com {store.loyalty_config?.min_points_redemption || 100} pontos, pode resgatar R$ {((store.loyalty_config?.min_points_redemption || 100) * (store.loyalty_config?.redemption_value || 0.01)).toFixed(2)} de desconto</li>
                        {(store.loyalty_config?.welcome_bonus || 0) > 0 && (
                          <li>• Novos clientes ganham {store.loyalty_config?.welcome_bonus} pontos de bônus!</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Theme */}
          <TabsContent value="theme">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#ff6b00]" />
                  Tema do Cardápio
                </CardTitle>
                <p className="text-sm text-white/50">Personalize as cores do seu cardápio digital</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Preset Themes */}
                <div>
                  <label className="text-sm text-white/60 mb-3 block">Escolha um tema</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {THEME_PRESETS.map(theme => (
                      <button
                        key={theme.id}
                        onClick={() => applyThemePreset(theme.id)}
                        className={`relative p-4 rounded-lg border-2 transition-all ${
                          store.theme_config?.preset === theme.id 
                            ? 'border-white' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                        style={{ backgroundColor: theme.background }}
                      >
                        {store.theme_config?.preset === theme.id && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center">
                            <Check className="w-3 h-3 text-black" />
                          </div>
                        )}
                        <div className="flex gap-1 mb-2">
                          <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.primary }}></div>
                          <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.secondary }}></div>
                          <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.card }}></div>
                        </div>
                        <p className="text-sm font-medium" style={{ color: theme.text }}>{theme.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="border-t border-white/10 pt-6">
                  <label className="text-sm text-white/60 mb-3 block">Personalizar cores</label>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Cor Primária</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={store.theme_config?.primary_color || '#ff6b00'}
                          onChange={(e) => {
                            updateThemeConfig('primary_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="w-12 h-10 rounded cursor-pointer bg-transparent"
                        />
                        <Input
                          value={store.theme_config?.primary_color || '#ff6b00'}
                          onChange={(e) => {
                            updateThemeConfig('primary_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="flex-1 bg-[#0f0f15] border-white/10 text-white uppercase"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Cor de Fundo</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={store.theme_config?.background_color || '#1a1a2e'}
                          onChange={(e) => {
                            updateThemeConfig('background_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="w-12 h-10 rounded cursor-pointer bg-transparent"
                        />
                        <Input
                          value={store.theme_config?.background_color || '#1a1a2e'}
                          onChange={(e) => {
                            updateThemeConfig('background_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="flex-1 bg-[#0f0f15] border-white/10 text-white uppercase"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Cor dos Cards</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={store.theme_config?.card_color || '#13131f'}
                          onChange={(e) => {
                            updateThemeConfig('card_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="w-12 h-10 rounded cursor-pointer bg-transparent"
                        />
                        <Input
                          value={store.theme_config?.card_color || '#13131f'}
                          onChange={(e) => {
                            updateThemeConfig('card_color', e.target.value);
                            updateThemeConfig('preset', 'custom');
                          }}
                          className="flex-1 bg-[#0f0f15] border-white/10 text-white uppercase"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Cores de Texto */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-white font-medium mb-3">Cores de Texto</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-white/40 mb-1 block">Texto do Cabeçalho</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={store.theme_config?.header_text_color || store.theme_config?.text_color || '#ffffff'}
                            onChange={(e) => {
                              updateThemeConfig('header_text_color', e.target.value);
                              updateThemeConfig('preset', 'custom');
                            }}
                            className="w-12 h-10 rounded cursor-pointer bg-transparent"
                          />
                          <Input
                            value={store.theme_config?.header_text_color || store.theme_config?.text_color || '#ffffff'}
                            onChange={(e) => {
                              updateThemeConfig('header_text_color', e.target.value);
                              updateThemeConfig('preset', 'custom');
                            }}
                            className="flex-1 bg-[#0f0f15] border-white/10 text-white uppercase"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-white/40 mb-1 block">Texto dos Cards</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={store.theme_config?.card_text_color || store.theme_config?.text_color || '#ffffff'}
                            onChange={(e) => {
                              updateThemeConfig('card_text_color', e.target.value);
                              updateThemeConfig('preset', 'custom');
                            }}
                            className="w-12 h-10 rounded cursor-pointer bg-transparent"
                          />
                          <Input
                            value={store.theme_config?.card_text_color || store.theme_config?.text_color || '#ffffff'}
                            onChange={(e) => {
                              updateThemeConfig('card_text_color', e.target.value);
                              updateThemeConfig('preset', 'custom');
                            }}
                            className="flex-1 bg-[#0f0f15] border-white/10 text-white uppercase"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="border-t border-white/10 pt-6">
                  <label className="text-sm text-white/60 mb-3 block">Pré-visualização</label>
                  <div 
                    className="rounded-lg p-4 border border-white/10"
                    style={{ backgroundColor: store.theme_config?.background_color || '#1a1a2e' }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                        style={{ 
                          backgroundColor: store.theme_config?.primary_color || '#ff6b00',
                          color: store.theme_config?.header_text_color || store.theme_config?.text_color || '#ffffff'
                        }}
                      >
                        {store.name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-bold" style={{ color: store.theme_config?.header_text_color || store.theme_config?.text_color || '#ffffff' }}>
                          {store.name || 'Sua Loja'}
                        </p>
                        <p className="text-xs" style={{ color: `${store.theme_config?.header_text_color || store.theme_config?.text_color || '#ffffff'}80` }}>
                          Cardápio Digital
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: store.theme_config?.card_color || '#13131f' }}
                      >
                        <div className="w-full h-12 rounded mb-2" style={{ backgroundColor: store.theme_config?.primary_color || '#ff6b00', opacity: 0.2 }}></div>
                        <p className="text-sm font-medium" style={{ color: store.theme_config?.card_text_color || store.theme_config?.text_color || '#ffffff' }}>Produto</p>
                        <p className="text-sm font-bold" style={{ color: store.theme_config?.primary_color || '#ff6b00' }}>R$ 10,00</p>
                      </div>
                      <div 
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: store.theme_config?.card_color || '#13131f' }}
                      >
                        <div className="w-full h-12 rounded mb-2" style={{ backgroundColor: store.theme_config?.primary_color || '#ff6b00', opacity: 0.2 }}></div>
                        <p className="text-sm font-medium" style={{ color: store.theme_config?.card_text_color || store.theme_config?.text_color || '#ffffff' }}>Produto</p>
                        <p className="text-sm font-bold" style={{ color: store.theme_config?.primary_color || '#ff6b00' }}>R$ 15,00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sound Settings */}
          <TabsContent value="sound">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-[#ff6b00]" />
                  Som de Notificação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sound Enabled Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <h4 className="text-white font-medium">Ativar Som</h4>
                    <p className="text-sm text-white/60">Tocar som quando chegar novo pedido</p>
                  </div>
                  <Switch
                    checked={store.notification_config?.sound_enabled !== false}
                    onCheckedChange={(checked) => setStore({
                      ...store,
                      notification_config: { ...store.notification_config, sound_enabled: checked }
                    })}
                    className="data-[state=checked]:bg-[#ff6b00]"
                  />
                </div>

                {/* Sound Type Selection */}
                <div className="space-y-3">
                  <label className="text-sm text-white/60">Escolha o Som</label>
                  <div className="grid gap-3">
                    {soundService.getSoundTypes().map(sound => (
                      <div 
                        key={sound.value}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          (store.notification_config?.sound_type || 'default') === sound.value
                            ? 'border-[#ff6b00] bg-[#ff6b00]/10'
                            : 'border-white/10 bg-[#0f0f15] hover:border-white/20'
                        }`}
                        onClick={() => setStore({
                          ...store,
                          notification_config: { ...store.notification_config, sound_type: sound.value }
                        })}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              (store.notification_config?.sound_type || 'default') === sound.value
                                ? 'border-[#ff6b00]'
                                : 'border-white/30'
                            }`}>
                              {(store.notification_config?.sound_type || 'default') === sound.value && (
                                <div className="w-2 h-2 rounded-full bg-[#ff6b00]" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{sound.label}</p>
                              <p className="text-xs text-white/50">{sound.description}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#ff6b00] hover:bg-[#ff6b00]/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              soundService.preview(sound.value);
                            }}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Testar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Volume Control */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-white/60">Volume</label>
                    <span className="text-sm text-white">{store.notification_config?.volume || 100}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <VolumeX className="w-4 h-4 text-white/40" />
                    <Slider
                      value={[store.notification_config?.volume || 100]}
                      onValueChange={(value) => {
                        setStore({
                          ...store,
                          notification_config: { ...store.notification_config, volume: value[0] }
                        });
                        soundService.setVolume(value[0]);
                      }}
                      max={100}
                      step={10}
                      className="flex-1"
                    />
                    <Volume2 className="w-4 h-4 text-white/40" />
                  </div>
                </div>

                {/* Test Button */}
                <div className="pt-4 border-t border-white/10">
                  <Button
                    onClick={() => {
                      soundService.setVolume(store.notification_config?.volume || 100);
                      soundService.play(store.notification_config?.sound_type || 'default');
                      toast.success('Som de teste reproduzido!');
                    }}
                    className="w-full bg-[#ff6b00] hover:bg-[#ff8533]"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Testar Som Atual
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Print Settings */}
          <TabsContent value="print">
            <Card className="card-adegga">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Printer className="w-5 h-5 text-[#ff6b00]" />
                  Impressão Automática
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cliente de Impressão Desktop */}
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg space-y-4">
                  <h4 className="text-green-400 font-medium flex items-center gap-2 text-lg">
                    <Download className="w-5 h-5" />
                    Aplicativo de Impressão Automática para Windows
                  </h4>
                  
                  <p className="text-sm text-white/80">
                    Baixe nosso aplicativo para Windows e imprima pedidos <strong className="text-green-400">automaticamente</strong>!
                    <br />
                    <span className="text-green-300">Não precisa instalar nada</span> - funciona direto no Windows!
                  </p>
                  
                  {/* Passo a Passo Simples */}
                  <div className="p-4 bg-[#0f0f15] rounded-lg space-y-3">
                    <h5 className="text-white font-medium">Como usar (3 passos):</h5>
                    <ol className="text-sm text-white/70 space-y-3 list-decimal list-inside">
                      <li>
                        <strong className="text-white">Baixe e extraia</strong> o arquivo ZIP abaixo
                      </li>
                      <li>
                        <strong className="text-white">Execute</strong> o arquivo <code className="bg-black/50 px-1 rounded">ADEGGA_Impressao.bat</code>
                        <span className="text-yellow-400"> (como Administrador!)</span>
                      </li>
                      <li>
                        <strong className="text-white">Faça login</strong> com seu email/senha e selecione a impressora
                      </li>
                    </ol>
                  </div>
                  
                  {/* Download Button */}
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                    onClick={() => window.open(`${API}/print-client/download`, '_blank')}
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Baixar Aplicativo de Impressão (.zip)
                  </Button>
                  
                  {/* Nota sobre funcionamento */}
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-sm text-blue-400">
                      <strong>Como funciona:</strong> O aplicativo fica aberto no seu computador e imprime 
                      automaticamente cada pedido que chega, sem precisar clicar em nada!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary px-8"
          >
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
